import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// Spotlight — one-shot post-evolution highlight on a newly added or
// renamed field. Mounts when the user lands on a screen via the
// ?screen=...&spotlight=... query params set by the BuildingScreen's
// Show me button. Anchors to [data-field-key="<key>"], shows a small
// gold card with a label, dismisses on click or after AUTO_DISMISS_MS.
//
// Best-effort: if the anchor doesn't appear within ANCHOR_TIMEOUT_MS
// (e.g. the screen uses a list/kanban layout without per-field anchors
// — see EVOLUTION-3e recon), dismiss silently rather than show a stuck
// orphan tooltip. Future polish could add screen-level fallback toast.
// ═══════════════════════════════════════════════════════════════════════

interface SpotlightProps {
  fieldKey: string;
  onDismiss: () => void;
}

const AUTO_DISMISS_MS = 8000;
const ANCHOR_POLL_MS = 100;
const ANCHOR_TIMEOUT_MS = 1000;

export function Spotlight({ fieldKey, onDismiss }: SpotlightProps) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [visible, setVisible] = useState(false);
  const elementRef = useRef<Element | null>(null);

  // Poll for the anchor element — the table may not have mounted yet
  // when SchemaRenderer mounts us. Cap at ANCHOR_TIMEOUT_MS so we don't
  // loop forever if the field really isn't anchorable on this screen.
  useEffect(() => {
    let elapsed = 0;
    const findAnchor = () => {
      const el = document.querySelector(`[data-field-key="${fieldKey}"]`);
      if (el) {
        elementRef.current = el;
        const rect = el.getBoundingClientRect();
        setAnchor(rect);
        setVisible(true);
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        return true;
      }
      return false;
    };

    if (findAnchor()) return;

    const interval = setInterval(() => {
      elapsed += ANCHOR_POLL_MS;
      if (findAnchor() || elapsed >= ANCHOR_TIMEOUT_MS) {
        clearInterval(interval);
        if (!elementRef.current) {
          onDismiss();
        }
      }
    }, ANCHOR_POLL_MS);

    return () => clearInterval(interval);
  }, [fieldKey, onDismiss]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => onDismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [visible, onDismiss]);

  // Re-measure on scroll/resize so the spotlight follows the anchor
  useEffect(() => {
    if (!visible || !elementRef.current) return;
    const update = () => {
      if (elementRef.current) {
        setAnchor(elementRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [visible]);

  if (!visible || !anchor) return null;

  // Position the card just below the anchor, clamped to the viewport.
  const cardWidth = 320;
  const top = Math.min(anchor.bottom + 12, window.innerHeight - 200);
  const rawLeft = anchor.left + anchor.width / 2 - cardWidth / 2;
  const left = Math.max(16, Math.min(rawLeft, window.innerWidth - cardWidth - 16));

  return (
    <>
      {/* Highlight ring around the anchor */}
      <div
        className="fixed pointer-events-none z-[450] rounded-md ring-4 ring-amber-400/60 animate-pulse"
        style={{
          top: anchor.top - 4,
          left: anchor.left - 4,
          width: anchor.width + 8,
          height: anchor.height + 8,
        }}
      />
      {/* Tooltip card */}
      <div
        className="fixed z-[451] bg-white rounded-xl shadow-2xl border border-amber-200 p-4"
        style={{ top, left, width: cardWidth }}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles size={16} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1">
              Just evolved
            </div>
            <p className="text-sm text-slate-900 leading-relaxed">
              Your Agentic Operating System added this. Click any cell to start using it.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
}
